import { memo, useCallback, useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import PropTypes from 'prop-types';
import get from 'transmute/get';
// @ts-expect-error module is not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import styled from 'styled-components';
import { getFromNumberDescription } from 'calling-ui-library/utils/phoneNumberSourceUtils';
import PortalIdParser from 'PortalIdParser';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallProvidersFromOmnibusResult, getConnectAccountNumbers, getHubSpotCallingEnabled, getHubspotProvidedNumbers, getHubspotSubAccountNumbers, getInstalledCallingSettings, getPhoneNumbers } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import { HUBSPOT, TWILIO } from 'calling-internal-common/call-provider/constants/ProviderNames';
import { getIsProviderHubSpot, getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { UpIcon, DownIcon } from 'foundations-assets';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { upgradeSalesStarterUrl } from 'calling-lifecycle-internal/utils/urlUtils';
import getAppUrls from 'calling-orchestration-shared-library/constants/appUrls';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DropdownMenuOption } from 'foundations-components/transitional/components/Dropdown';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import { hasPaidSeatAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_OPTIONS = 4;
const SectionTitle = styled.div.withConfig({
  displayName: "PhoneFromNumberOptions__SectionTitle",
  componentId: "qdsl2z-0"
})(["cursor:pointer;padding:", ";min-width:200px;background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}"], ({
  theme
}) => `${theme.space['200']} ${theme.space['0']}  ${theme.space['200']} ${theme.space['0']} `, ({
  theme
}) => `${theme.color['fill-secondary-subtle']}`);
const StyledHoverDiv = styled.div.withConfig({
  displayName: "PhoneFromNumberOptions__StyledHoverDiv",
  componentId: "qdsl2z-1"
})(["&:hover{background-color:", ";}button[aria-disabled='true']{span *{color:", ";}}button{span *{justify-content:flex-start;width:100%;}}"], ({
  theme
}) => `${theme.color['fill-secondary-subtle']}`, ({
  theme
}) => theme.color['text-core-disabled']);
const StyledUIList = styled.div.withConfig({
  displayName: "PhoneFromNumberOptions__StyledUIList",
  componentId: "qdsl2z-2"
})(["overflow-y:auto;max-height:calc((", " + 0.5) * 40px);border-bottom:", ";"], MAX_OPTIONS, ({
  theme,
  useborder
}) => useborder && `${theme.borderWidth['100']} solid
  ${theme.color['border-core-default']};`);
function getPhoneFromNumberOptions(providerName, accountNumbers, hasPaidSeat, isPaidPortal) {
  return accountNumbers.reduce((acc, fromNumber) => {
    const isHubSpotNumber = fromNumber.source === PhoneNumberSources.HUBSPOT_NUMBER;
    const key = {
      providerName,
      fromNumber
    };
    const disabled = isHubSpotNumber && (!hasPaidSeat || !isPaidPortal);
    acc.push({
      key,
      disabled
    });
    return acc;
  }, []);
}
function getOtherCallingProvidersOptions(callingProviders) {
  return callingProviders.reduce((acc, callingProvider) => {
    const providerName = callingProvider.get('name');
    if (getIsTwilioBasedCallProvider(callingProvider)) {
      return acc;
    }
    const key = {
      providerName
    };
    acc.push({
      key
    });
    return acc;
  }, []);
}
const OTHER_PROVIDERS = 'OtherProviders';
function PhoneFromNumberOptions({
  asyncSettingsOmnibus,
  selectedProviderName,
  selectedFromNumber,
  hasCallingProvidersCapability,
  onSelect,
  disabled,
  isUngatedForProviderSwitching
}) {
  const selectedOption = {
    providerName: selectedProviderName,
    fromNumber: selectedFromNumber
  };
  const selectedProvider = {
    name: selectedProviderName
  };
  const [showHubSpotSection, setShowHubSpotSection] = useState(getIsProviderHubSpot(selectedProvider));
  const [showTwilioSection, setShowTwilioSection] = useState(getIsTwilioBasedCallProvider(selectedProvider) && !getIsProviderHubSpot(selectedProvider));
  const [showOtherProvidersSection, setShowOtherProvidersSection] = useState(!isUngatedForProviderSwitching && !getIsTwilioBasedCallProvider(selectedProvider));
  const data = getData(asyncSettingsOmnibus);
  const fromNumbers = getPhoneNumbers(data);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(fromNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(fromNumbers);
  const hubspotNumbers = hubspotSubAccountNumbers.concat(hubspotProvidedNumbers);
  const hasPaidSeat = hasPaidSeatAccess();
  const isPaidPortal = hasCallingMinutesPerPortal();
  const hubspotOptions = useMemo(() => getPhoneFromNumberOptions(HUBSPOT, hubspotNumbers, hasPaidSeat, isPaidPortal), [hasPaidSeat, hubspotNumbers, isPaidPortal]);
  const connectAccountNumbers = getConnectAccountNumbers(fromNumbers);
  const twilioOptions = useMemo(() => getPhoneFromNumberOptions(TWILIO, connectAccountNumbers, hasPaidSeat, isPaidPortal), [connectAccountNumbers, hasPaidSeat, isPaidPortal]);
  const callingProviders = getCallProvidersFromOmnibusResult(data);
  const otherCallingProvidersOptions = useMemo(() => {
    return getOtherCallingProvidersOptions(callingProviders);
  }, [callingProviders]);
  const otherCallingProvidersError = useMemo(() => get('error', getInstalledCallingSettings(data)), [data]);
  const phoneFromNumberSections = useMemo(() => [...(!getHubSpotCallingEnabled(data) ? [] : [{
    key: HUBSPOT,
    title: 'callWidgetProviders.HubSpot.widgetTitle',
    options: hubspotOptions,
    providerName: HUBSPOT,
    show: showHubSpotSection,
    toggleShowSection: override => setShowHubSpotSection(override && !showHubSpotSection),
    disabled: !isPaidPortal || !hasPaidSeat
  }]), {
    key: TWILIO,
    title: 'callWidgetProviders.TwilioConnect.widgetTitle',
    options: twilioOptions,
    providerName: TWILIO,
    show: showTwilioSection,
    toggleShowSection: override => setShowTwilioSection(override && !showTwilioSection)
  }, ...(isUngatedForProviderSwitching ? [] : [{
    key: OTHER_PROVIDERS,
    title: 'callWidgetProviders.OtherProviders.widgetTitle',
    options: otherCallingProvidersOptions,
    show: showOtherProvidersSection,
    toggleShowSection: override => setShowOtherProvidersSection(override && !showOtherProvidersSection),
    error: otherCallingProvidersError && 'callWidgetProviders.OtherProviders.error'
  }])], [data, hubspotOptions, showHubSpotSection, isPaidPortal, hasPaidSeat, twilioOptions, showTwilioSection, isUngatedForProviderSwitching, otherCallingProvidersOptions, showOtherProvidersSection, otherCallingProvidersError]);
  const handleToggleSection = useCallback(key => {
    phoneFromNumberSections.map(section => {
      const toggle = section.key === key;
      return section.toggleShowSection(toggle);
    });
  }, [phoneFromNumberSections]);
  const portalId = useMemo(() => PortalIdParser.get(), []);
  const title = useMemo(() => {
    const {
      paidUsersManagementUrl
    } = getAppUrls();
    return isPaidPortal ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'phoneFromNumberOptions.seatRequiredTooltip_jsx',
      elements: {
        UILink: Link
      },
      options: {
        href: paidUsersManagementUrl,
        target: '_blank'
      }
    }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'phoneFromNumberOptions.upgradeTooltip_jsx',
      elements: {
        UILink: Link
      },
      options: {
        href: upgradeSalesStarterUrl(portalId),
        target: '_blank'
      }
    });
  }, [isPaidPortal, portalId]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: phoneFromNumberSections.map((section, index) => {
      const shouldShowWhenDisabled = section.key === HUBSPOT && !hasPaidSeat;
      if (!shouldShowWhenDisabled && !section.options.length && !section.error || !hasCallingProvidersCapability && section.key === OTHER_PROVIDERS) {
        return null;
      }
      const overflowAndNotLast = section.options.length > MAX_OPTIONS && index !== phoneFromNumberSections.length - 1;
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Tooltip, {
          title: title,
          disabled: !section.disabled,
          "data-test-id": `pre-call-section-${section.key}-tooltip`,
          children: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(SectionTitle, {
              onClick: () => !section.disabled && handleToggleSection(section.key),
              className: section.disabled ? 'is--disabled' : '',
              "data-selenium-test": `pre-call-section-${section.key}`,
              "data-unit-test": `pre-call-section-${section.key}`,
              children: /*#__PURE__*/_jsxs(Flex, {
                justifyContent: "space-between",
                paddingInline: "300",
                children: [/*#__PURE__*/_jsx(Text, {
                  typography: "body-125",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: section.title
                  })
                }), section.show ? /*#__PURE__*/_jsx(UpIcon, {}) : /*#__PURE__*/_jsx(DownIcon, {})]
              })
            }), section.show && (section.error ? /*#__PURE__*/_jsx(View, {
              maxInlineSize: "200px",
              paddingInline: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: section.error
              })
            }) :
            /*#__PURE__*/
            /* @bend-expect trellis-tokenized */
            _jsx(StyledUIList, {
              "data-selenium-test": `section-options-list-${section.key}`,
              useborder: overflowAndNotLast ? 1 : 0,
              children: section.options.map(option => {
                const key = option.key;
                const keyOption = key;
                const selectedKeyOption = selectedOption;
                const shouldDisableTooltip = !option.disabled;
                let selected = key === selectedOption;
                let keyString = key;
                let Message = null;
                const isUsingTwilioConnect = keyOption.providerName === TWILIO;
                if (keyOption.fromNumber) {
                  const fromNumber = keyOption.fromNumber;
                  const fromNumberString = fromNumber.get('friendlyName') || '';
                  const fromNumberValue = fromNumber.phoneNumber || '';
                  const fromNumberExtension = fromNumber.extension || '';
                  const selectedFromNumberString = selectedKeyOption.fromNumber && selectedKeyOption.fromNumber.get('friendlyName');
                  selected = keyOption.providerName === selectedKeyOption.providerName && fromNumberString === selectedFromNumberString;
                  keyString = `${keyOption.providerName}_${fromNumberString}`;
                  Message = fromNumber.nickname ? /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx("span", {
                      children: fromNumber.nickname
                    }), /*#__PURE__*/_jsxs(Text, {
                      as: "small",
                      display: "block",
                      children: [/*#__PURE__*/_jsx(FormattedPhoneNumber, {
                        value: isUsingTwilioConnect ? fromNumberString : fromNumberValue,
                        extension: isUsingTwilioConnect ? undefined : fromNumberExtension
                      }), ' ', "(", getFromNumberDescription(fromNumber.source), ")"]
                    })]
                  }) : /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(FormattedPhoneNumber, {
                      value: isUsingTwilioConnect ? fromNumberString : fromNumberValue,
                      extension: isUsingTwilioConnect ? undefined : fromNumberExtension
                    }), /*#__PURE__*/_jsx("div", {
                      children: option.text
                    }), /*#__PURE__*/_jsx(Text, {
                      as: "small",
                      display: "block",
                      children: getFromNumberDescription(fromNumber.source)
                    })]
                  });
                } else {
                  Message = keyOption.providerName;
                  selected = keyOption.providerName === selectedOption.providerName;
                  keyString = `${keyOption.providerName}`;
                }
                return /*#__PURE__*/_jsx(Tooltip, {
                  title: title,
                  disabled: shouldDisableTooltip,
                  placement: "bottom",
                  children: /*#__PURE__*/_jsx(StyledHoverDiv, {
                    children: /*#__PURE__*/_jsx(Flex, {
                      inlineSize: "100%",
                      justifyContent: "space-between",
                      children: /*#__PURE__*/_jsx(DropdownMenuOption, {
                        selected: selected,
                        onClick: () => onSelect(key),
                        disabled: disabled || option.disabled,
                        "data-selenium-test": `pre-call-option-${keyString}`,
                        cursor: "pointer"
                        /* @ts-expect-error dependent on trellis issue https://git.hubteam.com/HubSpot/foundations-components/issues/5685 */,
                        use: "unstyled",
                        padding: "300",
                        inlineSize: "100%",
                        children: Message
                      }, keyString)
                    })
                  })
                }, keyString);
              })
            }))]
          })
        })
      }, section.key);
    })
  });
}
PhoneFromNumberOptions.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedProviderName: PropTypes.string.isRequired,
  selectedFromNumber: RecordPropType('FromNumber'),
  hasCallingProvidersCapability: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};
export default /*#__PURE__*/memo(PhoneFromNumberOptions);